/**
 * Shared TrackCard component used on HomePage and LearnAndPractice.
 * Enhanced with sophisticated animations and refined interactions.
 */

import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TrackSummary, Difficulty } from '../../types';

export const DIFFICULTY_CONFIG: Record<string, { 
  color: string; 
  bg: string; 
  label: string; 
  description: string;
  gradient: string;
}> = {
  easy:   { 
    color: '#81b29a', 
    bg: 'rgba(129, 178, 154, 0.1)', 
    label: 'Beginner',     
    description: 'Fundamental patterns',
    gradient: 'linear-gradient(135deg, rgba(129, 178, 154, 0.2) 0%, rgba(129, 178, 154, 0.05) 100%)',
  },
  medium: { 
    color: '#f5a623', 
    bg: 'rgba(245, 166, 35, 0.1)',  
    label: 'Intermediate', 
    description: 'Moderate complexity',
    gradient: 'linear-gradient(135deg, rgba(245, 166, 35, 0.2) 0%, rgba(245, 166, 35, 0.05) 100%)',
  },
  hard:   { 
    color: '#e07a5f', 
    bg: 'rgba(224, 122, 95, 0.1)',  
    label: 'Advanced',     
    description: 'Complex rhythms',
    gradient: 'linear-gradient(135deg, rgba(224, 122, 95, 0.2) 0%, rgba(224, 122, 95, 0.05) 100%)',
  },
  expert: { 
    color: '#d4a574', 
    bg: 'rgba(212, 165, 116, 0.1)', 
    label: 'Expert',       
    description: 'Professional level',
    gradient: 'linear-gradient(135deg, rgba(212, 165, 116, 0.2) 0%, rgba(212, 165, 116, 0.05) 100%)',
  },
};

export interface TrackCardProps {
  track: TrackSummary;
  onPlay: () => void;
  isLoggedIn: boolean;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  index: number;
  isVisible: boolean;
}

export function TrackCard({ 
  track, 
  onPlay, 
  isLoggedIn, 
  isHovered, 
  onHover, 
  onLeave, 
  index, 
  isVisible 
}: TrackCardProps) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isPressed, setIsPressed] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const diff = (track.difficulty ?? 'medium') as Difficulty;
  const config = DIFFICULTY_CONFIG[diff];
  const durationStr = track.duration
    ? `${Math.floor(track.duration / 60)}:${String(Math.floor(track.duration % 60)).padStart(2, '0')}`
    : '--:--';

  // Track mouse position for spotlight effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const handleMouseDown = () => setIsPressed(true);
  const handleMouseUp = () => setIsPressed(false);

  // Calculate entrance animation delay
  const entranceDelay = index * 80;

  return (
    <div
      ref={cardRef}
      className="group relative rounded-2xl overflow-hidden cursor-pointer"
      onClick={onPlay}
      onMouseEnter={onHover}
      onMouseLeave={() => {
        onLeave();
        setIsPressed(false);
      }}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onPlay()}
      style={{
        background: 'linear-gradient(165deg, #111111 0%, #0a0a0a 50%, #0d0d0d 100%)',
        border: `1px solid ${isHovered ? config.color + '50' : '#1f1f20'}`,
        transform: isPressed 
          ? 'translateY(-4px) scale(0.98)' 
          : isHovered 
            ? 'translateY(-8px)' 
            : 'translateY(0)',
        boxShadow: isHovered 
          ? `0 20px 60px ${config.bg.replace('0.1', '0.3')}, 0 0 0 1px ${config.color}20` 
          : '0 4px 20px rgba(0,0,0,0.3)',
        opacity: isVisible ? 1 : 0,
        transition: `all 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${entranceDelay}ms, 
                     border-color 0.3s ease, 
                     box-shadow 0.3s ease,
                     transform 0.2s ease`,
      }}
    >
      {/* Spotlight glow effect following cursor */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: isHovered 
            ? `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, ${config.color}15 0%, transparent 50%)`
            : 'none',
        }}
      />

      {/* Top accent line with gradient */}
      <div 
        className="absolute top-0 left-0 right-0 h-0.5 transition-all duration-500"
        style={{ 
          background: `linear-gradient(90deg, transparent 0%, ${config.color} 50%, transparent 100%)`,
          opacity: isHovered ? 1 : 0.3,
          transform: isHovered ? 'scaleX(1)' : 'scaleX(0.5)',
        }}
      />

      {/* Ambient glow in corner */}
      <div 
        className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl transition-all duration-700 opacity-0 group-hover:opacity-100"
        style={{ background: config.color }}
      />

      <div className="relative p-6">
        {/* Header with difficulty badge and BPM */}
        <div className="flex items-start justify-between mb-6">
          <span
            className="px-3 py-1.5 rounded-full text-xs font-bold border transition-all duration-300"
            style={{ 
              color: config.color, 
              backgroundColor: isHovered ? config.bg : 'rgba(31, 31, 32, 0.5)', 
              borderColor: `${config.color}40`,
              boxShadow: isHovered ? `0 0 20px ${config.bg}` : 'none',
            }}
          >
            {config.label}
          </span>
          {track.bpm && (
            <span 
              className="text-stone-500 text-sm font-mono transition-colors duration-300"
              style={{ color: isHovered ? config.color : undefined }}
            >
              {Math.round(track.bpm)} <span className="text-stone-600">BPM</span>
            </span>
          )}
        </div>

        {/* Title with animated color change */}
        <h3
          className="text-2xl font-bold mb-2 transition-all duration-300"
          style={{ 
            color: isHovered ? config.color : '#f5f5f4',
            transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
          }}
        >
          {track.title}
        </h3>
        <p className="text-stone-500 mb-6 transition-colors duration-300 group-hover:text-stone-400">
          {track.artist ?? 'Unknown Artist'}
        </p>

        {/* Divider with animated gradient */}
        <div className="flex items-center justify-between pt-4 border-t border-studio-800">
          <span className="text-stone-600 text-sm font-mono">{durationStr}</span>
          <span 
            className="text-stone-600 text-sm transition-all duration-300"
            style={{ 
              color: isHovered ? config.color : undefined,
              transform: isHovered ? 'translateX(-4px)' : 'translateX(0)',
            }}
          >
            {config.description}
          </span>
        </div>
      </div>

      {/* Action overlay that slides up on hover */}
      <div 
        className="absolute inset-x-0 bottom-0 p-6 transition-all duration-500"
        style={{
          background: `linear-gradient(to top, ${config.bg.replace('0.1', '0.95')} 0%, ${config.bg.replace('0.1', '0.8')} 30%, transparent 100%)`,
          opacity: isHovered ? 1 : 0,
          transform: isHovered ? 'translateY(0)' : 'translateY(20px)',
        }}
      >
        <div className="flex gap-3">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPlay(); }}
            className="flex-1 py-3 rounded-xl font-bold text-studio-950 transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: config.color,
              boxShadow: `0 4px 20px ${config.bg.replace('0.1', '0.4')}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = 'brightness(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'brightness(1)';
            }}
          >
            {isLoggedIn ? (
              <>
                <span>Start Exercise</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </>
            ) : (
              <>
                <span>Sign In to Practice</span>
              </>
            )}
          </button>
          <Link
            to={`/leaderboard?track=${track.id}`}
            onClick={(e) => e.stopPropagation()}
            className="px-4 py-3 rounded-xl font-bold bg-studio-800 text-stone-300 transition-all duration-300 hover:bg-studio-700 hover:scale-105 active:scale-95 flex items-center justify-center"
            title="View Leaderboard"
            style={{
              border: `1px solid ${config.color}30`,
            }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </Link>
        </div>
      </div>

      {/* Corner decoration */}
      <div 
        className="absolute top-4 right-4 w-2 h-2 rounded-full transition-all duration-300"
        style={{
          background: config.color,
          opacity: isHovered ? 1 : 0,
          transform: isHovered ? 'scale(1)' : 'scale(0)',
          boxShadow: `0 0 10px ${config.color}`,
        }}
      />
    </div>
  );
}
